// Pre-connected windows ("Keep windows ready").
//
// A Link is a standing WebRTC connection between two friends who are both online
// with their cameras on. While nobody is there it carries nothing: the video and
// audio senders hold no track, so not a frame or a sample leaves either home. The
// only traffic is a presence message on a data channel ("someone is here" /
// "nobody is here") and the relay keepalives. When both sides report someone
// present, each side attaches its own camera and microphone to the senders it
// already has, and the window opens in a fraction of a second: no match on the
// server, no offer and answer, no connection to build.
//
// Privacy rules, enforced on the sending side and nowhere else:
//   1. A device sends its picture only while its own detector sees someone. The far
//      side can report what it likes; it cannot switch this camera on.
//   2. Links use the TURN relay only, so a friend's device never learns this
//      device's network address while the window is closed.
//   3. Links exist only between mutual, unpaused friends, and only while both have
//      "Keep windows ready" on. Turning it off leaves the classic path: match on the
//      server, then call.
//
// Signalling goes through the relay Worker room "<lowUid>_<highUid>_link" (the
// Worker admits only those two uids). The side with the lower uid makes every
// offer; the other side only answers, so no two offers ever collide.
import { writable, get } from "svelte/store";
import { openSignalChannel, signalingEnabled } from "./signaling.js";
import { getRtcConfiguration } from "./turn.js";
import { tuneVideoSender } from "./encoding.js";
import { callActive } from "./callState.js";

const HEARTBEAT_MS = 10000;
const PRESENCE_STALE_MS = 35000;
const RETRY_MIN_MS = 3000;
const RETRY_MAX_MS = 60000;

function stamp() {
  return new Date().toISOString().slice(11, 23);
}

export function linkRoomId(a, b) {
  return (a < b ? [a, b] : [b, a]).join("_") + "_link";
}

/** One standing connection to one friend. */
class Link {
  constructor({ me, friend, user, getStream, onChange, log }) {
    this.me = me;
    this.friend = friend;
    this.user = user;
    this.getStream = getStream;
    this.onChange = onChange || (() => {});
    this.log = log || ((...a) => console.log(stamp(), "link", friend.slice(0, 6), ...a));
    this.offerer = me < friend;
    this.roomId = linkRoomId(me, friend);
    this.state = "connecting";       // connecting | ready | failed | closed
    this.remote = { present: false, busy: false, seenAt: 0 };
    this.local = { present: false, busy: false };
    this.sending = false;
    this.remoteStream = new MediaStream();
    this.channel = null;
    this.pc = null;
    this.dc = null;
    this.senders = { video: null, audio: null };
    this.closed = false;
    this.retryDelay = RETRY_MIN_MS;
    this.retryTimer = null;
    this.heartbeat = null;
    this.staleTimer = null;
    this.offerId = 0;
    this.pendingCandidates = [];
    this.haveRemote = false;
  }

  async start() {
    if (this.closed) return;
    try {
      await this.ensureChannel();
      await this.buildPeer();
    } catch (err) {
      this.log("start failed:", err && err.message ? err.message : err);
      this.scheduleRetry();
    }
  }

  async ensureChannel() {
    if (this.channel) return;
    const channel = await openSignalChannel(this.roomId, this.user);
    if (this.closed) { channel.close(); return; }
    this.channel = channel;
    channel.onMessage((message) => this.onSignal(message));
    channel.onClose(() => {
      if (this.closed) return;
      this.log("signalling socket closed");
      this.channel = null;
      // The connection itself may well survive; the socket is only needed to
      // renegotiate. Reopen it so a later ICE restart has a path.
      this.scheduleRetry(true);
    });
  }

  buildPeer() {
    if (this.closed) return Promise.resolve();
    this.building = this._buildPeer().finally(() => { this.building = null; });
    return this.building;
  }

  async _buildPeer() {
    this.teardownPeer();
    // Fresh relay credentials every time: a rebuild is how an expired set is replaced.
    const configuration = await getRtcConfiguration(this.user, { forceRelay: true });
    if (this.closed) return;
    if (configuration.iceTransportPolicy !== "relay") {
      throw new Error("no TURN credentials; a link needs the relay");
    }
    const pc = new RTCPeerConnection(configuration);
    this.pc = pc;
    this.haveRemote = false;
    this.pendingCandidates = [];
    // Both directions are negotiated once, with nothing attached. Opening a window
    // later is replaceTrack() on these senders: no renegotiation. Only the offering
    // side creates the transceivers; a remote offer never adopts transceivers made
    // with addTransceiver, so the answering side takes the ones the offer creates
    // (see adoptTransceivers).
    if (this.offerer) {
      pc.addTransceiver("video", { direction: "sendrecv" });
      pc.addTransceiver("audio", { direction: "sendrecv" });
      this.adoptTransceivers();
    }
    const dc = pc.createDataChannel("presence", { negotiated: true, id: 0 });
    this.dc = dc;
    dc.onopen = () => {
      this.log("presence channel open");
      this.sendPresence();
      clearInterval(this.heartbeat);
      this.heartbeat = setInterval(() => this.sendPresence(), HEARTBEAT_MS);
    };
    dc.onmessage = (event) => this.onPresence(event.data);
    dc.onclose = () => { clearInterval(this.heartbeat); this.heartbeat = null; };

    pc.addEventListener("icecandidate", (event) => {
      if (!event.candidate || !this.channel) return;
      this.channel.send({ type: "candidate", candidate: event.candidate.toJSON() });
    });
    pc.addEventListener("track", (event) => {
      const track = event.track;
      this.remoteStream.getTracks().filter((t) => t.kind === track.kind).forEach((t) => this.remoteStream.removeTrack(t));
      this.remoteStream.addTrack(track);
      try { if (event.receiver && "playoutDelayHint" in event.receiver) event.receiver.playoutDelayHint = 0.1; } catch (e) { /* unsupported */ }
      this.onChange(this);
    });
    pc.addEventListener("connectionstatechange", () => {
      const s = pc.connectionState;
      this.log("connection", s);
      if (s === "connected") {
        this.state = "ready";
        this.retryDelay = RETRY_MIN_MS;
      } else if (s === "failed") {
        this.state = "failed";
        if (this.offerer) this.scheduleRetry(); else this.send({ type: "restart" });
      } else if (s === "disconnected") {
        this.state = "connecting";
      }
      this.onChange(this);
    });
    pc.addEventListener("iceconnectionstatechange", () => {
      if (pc.iceConnectionState === "failed" && this.offerer) this.scheduleRetry();
    });
    if (this.offerer) {
      // The answering side drops its old connection and builds a new one before
      // this offer reaches it (messages on the socket stay in order).
      this.send({ type: "reset" });
      await this.offer();
    }
  }

  /** Take the senders of the negotiated transceivers, one per kind, as sendrecv. */
  adoptTransceivers() {
    if (!this.pc) return;
    this.senders = { video: null, audio: null };
    for (const t of this.pc.getTransceivers()) {
      const kind = t.receiver && t.receiver.track ? t.receiver.track.kind : null;
      if (!kind || this.senders[kind] || t.stopped) continue;
      try { t.direction = "sendrecv"; } catch (e) { /* read-only in some states */ }
      this.senders[kind] = t.sender;
      if (kind === "video") tuneVideoSender(t.sender);
    }
    // A window that was open when the connection was rebuilt keeps sending.
    if (this.sending) { this.sending = false; this.setSending(true); }
  }

  teardownPeer() {
    clearInterval(this.heartbeat);
    this.heartbeat = null;
    if (this.dc) { try { this.dc.close(); } catch (e) { /* ignore */ } this.dc = null; }
    if (this.pc) { try { this.pc.close(); } catch (e) { /* ignore */ } this.pc = null; }
    this.senders = { video: null, audio: null };
    this.sending = false;
    this.remote = { present: false, busy: false, seenAt: 0 };
  }

  async offer(iceRestart = false) {
    if (!this.pc || !this.channel) return;
    this.offerId += 1;
    const id = this.offerId;
    const offer = await this.pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
    if (!this.pc || this.offerId !== id) return;
    await this.pc.setLocalDescription(offer);
    this.send({ type: "offer", offerId: id, description: { type: offer.type, sdp: offer.sdp } });
  }

  /** Rebuild after a delay that doubles on repeated failures. */
  scheduleRetry(channelOnly = false) {
    if (this.closed || this.retryTimer) return;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS);
    this.retryTimer = setTimeout(async () => {
      this.retryTimer = null;
      try {
        await this.ensureChannel();
        const dead = !this.pc || this.pc.connectionState === "failed" || this.pc.connectionState === "closed";
        if (!channelOnly || dead) {
          if (this.offerer) await this.buildPeer();
          else this.send({ type: "restart" }); // the offering side rebuilds and resets us
        }
      } catch (err) {
        this.log("retry failed:", err && err.message ? err.message : err);
        this.scheduleRetry(channelOnly);
      }
    }, delay);
  }

  send(message) {
    if (this.channel) this.channel.send(message);
  }

  async onSignal(message) {
    if (this.closed || !message) return;
    try {
      if (message.type === "peer-joined") {
        // The friend (re)connected to the room. If we are not connected to them,
        // start over so both sides agree; a live connection is left alone.
        if (this.offerer && (!this.pc || this.pc.connectionState !== "connected")) await this.buildPeer();
        return;
      }
      if (message.type === "peer-left") {
        this.remote = { present: false, busy: false, seenAt: 0 };
        this.onChange(this);
        return;
      }
      if (message.type === "restart") {
        if (this.offerer) { this.retryDelay = RETRY_MIN_MS; this.scheduleRetry(); }
        return;
      }
      if (message.type === "reset") {
        if (!this.offerer) await this.buildPeer();
        return;
      }
      if (this.building) await this.building;
      if (!this.pc) return;
      if (message.type === "offer" && !this.offerer) {
        await this.pc.setRemoteDescription(new RTCSessionDescription(message.description));
        this.haveRemote = true;
        this.adoptTransceivers();
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.send({ type: "answer", offerId: message.offerId, description: { type: answer.type, sdp: answer.sdp } });
        await this.flushCandidates();
      } else if (message.type === "answer" && this.offerer) {
        if (message.offerId !== this.offerId || this.pc.signalingState !== "have-local-offer") return; // stale
        await this.pc.setRemoteDescription(new RTCSessionDescription(message.description));
        this.haveRemote = true;
        await this.flushCandidates();
      } else if (message.type === "candidate") {
        if (this.haveRemote) await this.addCandidate(message.candidate); else this.pendingCandidates.push(message.candidate);
      }
    } catch (err) {
      this.log("signalling error:", err && err.message ? err.message : err);
    }
  }

  async flushCandidates() {
    const list = this.pendingCandidates.splice(0);
    for (const c of list) await this.addCandidate(c);
  }

  async addCandidate(c) {
    try { await this.pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { /* stale candidate */ }
  }

  // ---- presence ------------------------------------------------------------

  setLocal(present, busy) {
    const changed = present !== this.local.present || busy !== this.local.busy;
    this.local = { present: !!present, busy: !!busy };
    if (changed) this.sendPresence();
  }

  sendPresence() {
    if (!this.dc || this.dc.readyState !== "open") return;
    try { this.dc.send(JSON.stringify({ present: this.local.present, busy: this.local.busy })); } catch (e) { /* closed */ }
  }

  onPresence(data) {
    let message;
    try { message = JSON.parse(data); } catch (e) { return; }
    if (!message || typeof message !== "object") return;
    const before = this.remote;
    this.remote = { present: !!message.present, busy: !!message.busy, seenAt: Date.now() };
    clearTimeout(this.staleTimer);
    this.staleTimer = setTimeout(() => {
      if (this.closed) return;
      this.remote = { present: false, busy: false, seenAt: 0 };
      this.onChange(this);
    }, PRESENCE_STALE_MS);
    if (before.present !== this.remote.present || before.busy !== this.remote.busy) {
      this.log("friend", this.remote.present ? "present" : "away", this.remote.busy ? "(busy)" : "");
      this.onChange(this);
    }
  }

  // ---- sending -------------------------------------------------------------

  /** Attach (or detach) this device's own camera and microphone. */
  async setSending(on) {
    if (this.sending === !!on) return;
    const stream = on ? this.getStream() : null;
    if (on && !stream) return;
    this.sending = !!on;
    const video = stream ? stream.getVideoTracks()[0] || null : null;
    const audio = stream ? stream.getAudioTracks()[0] || null : null;
    try {
      if (this.senders.video) await this.senders.video.replaceTrack(video);
      if (this.senders.audio) await this.senders.audio.replaceTrack(audio);
      // Negotiation is long done here, so browsers that had no encodings to cap at
      // build time (Firefox) take the limit now.
      if (on && this.senders.video) tuneVideoSender(this.senders.video);
      this.log(on ? "sending picture and sound" : "sending nothing");
    } catch (err) {
      this.sending = false;
      this.log("replaceTrack failed:", err && err.message ? err.message : err);
    }
    this.onChange(this);
  }

  close() {
    this.closed = true;
    clearTimeout(this.retryTimer);
    clearTimeout(this.staleTimer);
    this.teardownPeer();
    if (this.channel) { this.channel.send({ type: "bye" }); this.channel.close(); this.channel = null; }
    this.remoteStream.getTracks().forEach((t) => this.remoteStream.removeTrack(t));
    this.state = "closed";
    this.onChange(this);
  }
}

/**
 * Keeps one Link per friend who is online and ready, and opens the one window
 * that both sides agree on. `links` is a store of plain summaries for the UI;
 * `openWith` is the uid of the friend whose window is open, or null.
 */
export function createLinkManager({ user, db, getStream, log }) {
  const me = user.uid;
  const links = new Map();          // friend uid -> Link
  const summaries = writable([]);
  const openWith = writable(null);
  let present = false;
  let paused = [];
  let approved = [];
  let statusUnsubs = new Map();     // friend uid -> unsubscribe
  let friendshipUnsub = null;
  let stopped = false;
  const say = log || ((...a) => console.log(stamp(), "links:", ...a));

  function publish() {
    summaries.set([...links.values()].map((l) => ({
      friend: l.friend,
      state: l.state,
      remotePresent: l.remote.present,
      remoteBusy: l.remote.busy,
      sending: l.sending,
      remoteStream: l.remoteStream,
    })));
  }

  function onLinkChange() {
    evaluate();
    publish();
  }

  function busyFor(friend) {
    const open = get(openWith);
    return (open !== null && open !== friend) || (open === null && get(callActive));
  }

  function evaluate() {
    if (stopped) return;
    const open = get(openWith);
    // Keep the open one open while both are still there.
    if (open !== null) {
      const l = links.get(open);
      const still = l && present && l.remote.present && l.state !== "closed";
      if (!still) {
        if (l) l.setSending(false);
        openWith.set(null);
        say("window closed with", open.slice(0, 6));
      }
    }
    if (get(openWith) === null && !get(callActive)) {
      for (const l of links.values()) {
        if (present && l.remote.present && !l.remote.busy && l.state === "ready") {
          openWith.set(l.friend);
          say("window opening with", l.friend.slice(0, 6));
          l.setSending(true);
          break;
        }
      }
    }
    for (const l of links.values()) l.setLocal(present, busyFor(l.friend));
  }

  function ensureLink(friend) {
    if (links.has(friend) || stopped) return;
    say("linking with", friend.slice(0, 6));
    const link = new Link({ me, friend, user, getStream, onChange: onLinkChange });
    links.set(friend, link);
    link.setLocal(present, busyFor(friend));
    link.start();
    publish();
  }

  function dropLink(friend) {
    const link = links.get(friend);
    if (!link) return;
    say("unlinking", friend.slice(0, 6));
    links.delete(friend);
    link.close();
    if (get(openWith) === friend) openWith.set(null);
    publish();
  }

  function watchStatus(friend) {
    if (statusUnsubs.has(friend)) return;
    const unsub = db.collection("Status").doc(friend).onSnapshot(
      (snap) => {
        const d = snap.exists ? snap.data() : null;
        const wanted = !!(d && d.online && d.ready) && !paused.includes(friend);
        if (wanted) ensureLink(friend); else dropLink(friend);
      },
      (err) => {
        say("cannot read a friend's status (rules not deployed?):", err && err.message ? err.message : err);
      }
    );
    statusUnsubs.set(friend, unsub);
  }

  function unwatchStatus(friend) {
    const unsub = statusUnsubs.get(friend);
    if (unsub) unsub();
    statusUnsubs.delete(friend);
    dropLink(friend);
  }

  function start() {
    if (!signalingEnabled()) { say("relay not configured; links off"); return; }
    friendshipUnsub = db.collection("Friendships").doc(me).onSnapshot((snap) => {
      const d = snap.exists ? snap.data() : {};
      approved = Array.isArray(d.approved) ? d.approved.filter((id) => typeof id === "string" && id && id !== me) : [];
      paused = Array.isArray(d.paused) ? d.paused : [];
      for (const friend of [...statusUnsubs.keys()]) if (!approved.includes(friend)) unwatchStatus(friend);
      for (const friend of approved) {
        if (paused.includes(friend)) dropLink(friend);
        watchStatus(friend);
      }
      // A newly paused friend keeps its status listener but loses its link.
      for (const friend of paused) dropLink(friend);
    }, (err) => say("cannot read friendships:", err && err.message ? err.message : err));
  }

  function setPresent(value) {
    if (present === !!value) return;
    present = !!value;
    evaluate();
    publish();
  }

  function stop() {
    stopped = true;
    if (friendshipUnsub) friendshipUnsub();
    for (const friend of [...statusUnsubs.keys()]) unwatchStatus(friend);
    openWith.set(null);
    publish();
  }

  /** Bytes and frames in and out per link, for the console (PassCallLinks.stats()). */
  async function stats() {
    const out = {};
    for (const l of links.values()) {
      const row = { state: l.state, sending: l.sending, remotePresent: l.remote.present, remoteBusy: l.remote.busy };
      if (l.pc) {
        const reports = await l.pc.getStats();
        reports.forEach((r) => {
          if (r.type === "inbound-rtp" && r.kind === "video") Object.assign(row, { inBytes: r.bytesReceived, inFrames: r.framesDecoded, inFps: r.framesPerSecond });
          if (r.type === "outbound-rtp" && r.kind === "video") Object.assign(row, { outBytes: r.bytesSent, outFrames: r.framesEncoded });
        });
        row.senderTrack = l.senders.video && l.senders.video.track ? l.senders.video.track.readyState : "none";
        row.receiverTracks = l.pc.getReceivers().map((r) => r.track.kind + ":" + (r.track.muted ? "muted" : "live")).join(",");
        row.remoteStreamTracks = l.remoteStream.getTracks().map((t) => t.kind + ":" + t.readyState + (t.muted ? "/muted" : "")).join(",");
      }
      out[l.friend.slice(0, 6)] = row;
    }
    console.table(out);
    return out;
  }

  return { start, stop, setPresent, links: summaries, openWith, evaluate, stats };
}
